import 'tailwindcss/tailwind.css';

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CartesianGrid, ComposedChart, Dot, Line, ReferenceArea, Scatter, Tooltip, XAxis, YAxis } from 'recharts';

import { Box, Card, Divider, Grid, Stack, Typography } from '@mui/material';
import { isEmptyOrUndefined } from '@zippeditoolsjs/blocks';

import { BaseColors } from './constants';
import { getColorClassNames, tremorTwMerge } from './lib';
import { colorPalette, themeColorRange } from './theme';
import { capitalizeFirstLetter, hasOnlyOneValueForThisKey } from './utils';

const constructCategoryColors = (categories, colors) => {
  const categoryColors = new Map();
  categories.forEach((category, idx) => {
    categoryColors.set(category, colors[idx]);
  });
  return categoryColors;
};

const ScatterChartComponent = (props) => {
  let {
    points,
    styleColors = {},
    yAxisValueName = {},
    showTooltip = true,
    referenceAreas = [],
    onValueChange = () => { },
    heightComposedChart = 300,
    widthComposedChart = 2500,
    linesInfo = [{ lineName: 'line_y', lineKey: 'line_y' }],
    scatterInfo = [{ scatterName: 'scatter', scatterKey: 'scatter' }],
  } = props;
  const { t } = useTranslation();

  const categoryColors = constructCategoryColors(['actions'], themeColorRange);
  const [activeDot, setActiveDot] = useState(undefined);
  const [activeLegend, setActiveLegend] = useState(undefined);
  const [margin, setMargin] = useState({ top: 10, right: 70, bottom: 30, left: 10 });
  const hasOnValueChange = !!onValueChange;

  useEffect(() => {
    if (points?.length) {
      handleMargin(points[0].x);
    }
  }
    , [points]);


  // handlers
  const handleScatterClick = (event) => {
    onValueChange?.({
      eventType: "scatter",
      ...event.payload,
    });
  }

  const handlePayloadValue = (payload, totalElements, index) => {
    let tooltipDescription = '';
    if (totalElements - index === 1) {
      tooltipDescription = payload?.z;
    } else {
      tooltipDescription = payload?.z;
    }
    return tooltipDescription;
  }

  const onDotClick = (itemData, event) => {
    event.stopPropagation();
    if (!hasOnValueChange) return;
    if (
      (itemData.index === activeDot?.index && itemData.dataKey === activeDot?.dataKey) ||
      (hasOnlyOneValueForThisKey(points, itemData.dataKey) &&
        activeLegend &&
        activeLegend === itemData.dataKey)
    ) {
      setActiveLegend(undefined);
      setActiveDot(undefined);
      onValueChange?.(null);
    } else {
      setActiveLegend(itemData.dataKey);
      setActiveDot({
        index: itemData.index,
        dataKey: itemData.dataKey,
      });
      onValueChange?.({
        eventType: "dot",
        categoryClicked: itemData.dataKey,
        ...itemData.payload,
      });
    }
  }

  const handleStyleColor = (payload, payloadItem, targetStyle, defaultColor = '#6b7280') => {
    const styles = payload?.styles;
    let color = defaultColor;
    if (styles) {
      if (targetStyle) {
        color = styles?.[targetStyle] ?? color;
      } else {
        if (styles.alert) {
          color = styles.alertDotFill;
        }
      }
    }
    return color
  }

  const handleMargin = (x) => {
    const xLength = CustomXTickFormatter(x).length;
    const visibilityRatioY = 5.3;
    const visibilityRatioX = 6.4;
    const maxNameLength = Object.values(yAxisValueName).reduce((acc, curr) => curr?.name?.length > acc ? curr?.name?.length : acc, 0);
    const left = Math.max(xLength, maxNameLength) * visibilityRatioX;
    const top = 10;
    setMargin({ top, right: 70, bottom: xLength * visibilityRatioY, left });
  }

  const CustomXTickFormatter = (timeString) => {
    const xTime = timeString.split(' ');
    return xTime[1];
  };

  const CustomYTickFormatter = (value) => {
    return yAxisValueName[value]?.name ?? value;
  }

  const handleTicks = () => {
    const ticks = Object.keys(yAxisValueName);
    if (ticks.length > 1) {
      return ticks;
    } else if (ticks.length === 1) {
      // '-10' magic number is used to make sure the chart is not empty and the y-axis is visible
      return ['-10', ticks[0]];
    }
    return null;
  }

  return (
    !isEmptyOrUndefined(points, 'array') ?
      <Box sx={{ height: 'fit-content', width: 'max-content' }}>
        <ComposedChart
          width={widthComposedChart}
          height={heightComposedChart}
          data={points}
          margin={margin}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="x"
            type="category"
            name="Time"
            scale="point"
            angle={-45}
            textAnchor="end"
            tickFormatter={CustomXTickFormatter}
          />
          <YAxis
            type="number"
            name="Action"
            scale={'auto'}
            dataKey={scatterInfo[0]?.scatterKey}
            tickFormatter={CustomYTickFormatter}
            ticks={handleTicks()}
          />
          {/* Tooltip */}
          <Tooltip
            wrapperStyle={{ outline: "none" }}
            isAnimationActive={false}
            cursor={{ stroke: "#d1d5db", strokeWidth: 1 }}
            content={
              showTooltip ? (
                ({ active, payload, label }) => (
                  payload.map((payloadItem, index) => (
                    <Stack key={index} component={Card} minWidth='15em' divider={<Divider flexItem orientation='horizontal' />}>
                      {/* Timestamp */}
                      <Stack direction='row' px={2} py={1} alignItems='center'>
                        <Typography>{label}</Typography>
                        <Typography variant='caption' color='GrayText' pt={0.5} ml={0.7}>{t('overseer_app.general.local_time', 'local time')}</Typography>
                      </Stack>
                      {/* Legend */}
                      <Stack direction='row' px={2} py={1} component={Grid} container>
                        <Grid container item xs alignItems='center' flexGrow={1} minWidth='7em'>
                          {/* Legend icon */}
                          <svg
                            className={tremorTwMerge("flex-none h-2 w-2 mr-1.5")}
                            style={{ color: handleStyleColor(payloadItem.payload, payloadItem, payloadItem?.name, payloadItem?.color) }}
                            fill="currentColor"
                            viewBox="0 0 8 8"
                          >
                            <circle cx={4} cy={4} r={4} />
                          </svg>
                          {/* Name */}
                          {capitalizeFirstLetter(payloadItem.name)}
                        </Grid>
                        {/* Description */}
                        <Grid item>
                          {handlePayloadValue(payloadItem.payload, payload?.length, index)}
                        </Grid>
                      </Stack>
                    </Stack>
                  ))
                )
              ) : (
                <></>
              )
            }
            position={{ y: 0 }}
          />
          {/* Reference Areas */}
          {referenceAreas.map((area, index) => (
            <ReferenceArea
              key={`scatter-area-${index}`}
              x1={area.x1}
              x2={area.x2}
              label={{ value: area.action, position: 'insideTopRight' }}
              fill={area.fill ?? '#82ca9d'}
              opacity={area.opacity ?? 0.3}
            />
          ))}
          {/* Scatter Charts */}
          {scatterInfo.map(({ scatterName, scatterKey }, index) => (
            <Scatter
              onClick={handleScatterClick}
              key={`scatter-${index}-name`}
              name={scatterName}
              dataKey={scatterKey}
              color={styleColors?.[scatterKey] ?? '#8884d8'}
              fill={styleColors?.[scatterKey] ?? '#8884d8'} />
          ))}
          {/* Lines Charts */}
          {linesInfo.map(({ lineName, lineKey, connectNulls }, index) => (
            <Line
              connectNulls={connectNulls ?? true}
              key={index}
              type="step"
              dataKey={lineKey}
              name={lineName}
              stroke={styleColors?.[lineKey] ?? '#8884d8'}
              color={styleColors?.[lineKey] ?? '#8884d8'}
              strokeWidth={2}
              dot={(props) => {
                const { cx, cy, strokeLinecap, strokeLinejoin, payload } = props;
                return (
                  <Dot
                    key={cx} // Timestamp as key
                    cx={cx}
                    cy={cy}
                    r={3}
                    fill={handleStyleColor(payload, null, lineName)}
                    stroke={handleStyleColor(payload, null, lineName)}
                    strokeLinecap={strokeLinecap}
                    strokeLinejoin={strokeLinejoin}
                    strokeWidth={1}
                  />)
              }}
              // Dot when hovered over
              activeDot={(props) => {
                const { cx, cy, stroke, strokeLinecap, strokeLinejoin, strokeWidth, dataKey } = props;
                return (
                  <Dot
                    className={tremorTwMerge(
                      "stroke-tremor-background dark:stroke-dark-tremor-background",
                      onValueChange ? "cursor-pointer" : "",
                      getColorClassNames(
                        categoryColors.get(dataKey) ?? BaseColors.Gray,
                        colorPalette.text,
                      ).fillColor,
                    )}
                    cx={cx}
                    cy={cy}
                    r={5}
                    fill=""
                    stroke={stroke}
                    strokeLinecap={strokeLinecap}
                    strokeLinejoin={strokeLinejoin}
                    strokeWidth={strokeWidth}
                    onClick={(dotProps, event) => onDotClick(props, event)}
                  />
                );
              }}
            />
          ))}
        </ComposedChart>
      </Box>
      :
      <Box sx={{
        minHeight: '12em',
        background: '#D9D9D9',
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}>
        <Typography>{t('overseer_app.general.no_data', 'No data to display')}</Typography>
      </Box>
  );
};

export default ScatterChartComponent;
